import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Modal, Button, SegmentedControl, TextField, Loader, notifications, } from "@/shared/ui";
import { createShare, uploadSharePhoto, getSharePresets, } from "@/shared/api";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import { SharePreview } from "../SharePreview";
import { ShareStage } from "../ShareStage";
import { ShareSelect } from "../ShareSelect";
import { emptyPresets } from "../../lib/resolveConfig";
import { preloadShareFonts } from "../../lib/shareFonts";
import { initialEditorState, defaultEditorState, buildConfig, validateEditor, MAX_TEXT, MAX_PHOTOS, } from "./editorState";
import { Section, ColorField, TextChoiceField, TitleChoiceField, ImagePresetPicker, PatternControls, PhotoPicker, } from "./fields";
import styles from "./ShareEditorModal.module.scss";
/** Лимит фото в редакторе (как на бэке — до 20). */
const EDITOR_PHOTO_LIMIT = MAX_PHOTOS;
/**
 * Ужимает children целиком, чтобы они влезли в фиксированный viewport
 * (мини-превью на экране «Готово!»).
 */
const FitScale = ({ children, className, }) => {
    const viewportRef = React.useRef(null);
    const contentRef = React.useRef(null);
    const [scale, setScale] = React.useState(1);
    React.useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (!viewport || !content)
            return;
        const update = () => {
            const prev = content.style.transform;
            content.style.transform = "scale(1)";
            const vw = viewport.clientWidth;
            const vh = viewport.clientHeight;
            const cw = Math.max(content.scrollWidth, content.offsetWidth, 1);
            const ch = Math.max(content.scrollHeight, content.offsetHeight, 1);
            content.style.transform = prev;
            setScale(Math.min(1, vw / cw, vh / ch));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(viewport);
        ro.observe(content);
        const imgs = content.querySelectorAll("img");
        imgs.forEach((img) => {
            if (!img.complete)
                img.addEventListener("load", update, { once: true });
        });
        return () => ro.disconnect();
    }, [children]);
    return (_jsx("div", { ref: viewportRef, className: className, children: _jsx("div", { ref: contentRef, className: styles.fitScaleInner, style: { transform: `scale(${scale})` }, children: children }) }));
};
/** Достаёт человекочитаемую причину из ошибки axios (422 detail и т.п.). */
function extractError(e) {
    const err = e;
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string")
        return detail;
    if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        if (first?.msg)
            return first.msg;
    }
    if (status === 400)
        return "Песня ещё не готова или превышен лимит фото.";
    if (status === 403)
        return "Это аудио не принадлежит вам.";
    if (status === 422)
        return "Проверьте оформление — какое-то поле заполнено неверно.";
    return "Что-то пошло не так. Попробуйте ещё раз.";
}
export const ShareEditorModal = ({ opened, onClose, audioId, audioTitle, audioUrl, audioCoverUrl, useInstrumental = false, }) => {
    const [state, setState] = React.useState(() => initialEditorState());
    const [photos, setPhotos] = React.useState([]);
    const [presets, setPresets] = React.useState(emptyPresets);
    const [presetsLoading, setPresetsLoading] = React.useState(false);
    const [phase, setPhase] = React.useState("edit");
    const [shareUrl, setShareUrl] = React.useState("");
    const patch = React.useCallback((p) => setState((s) => ({ ...s, ...p })), []);
    // Сброс при каждом открытии + загрузка каталога пресетов.
    React.useEffect(() => {
        if (!opened)
            return;
        setState(initialEditorState());
        setPhotos([]);
        setPhase("edit");
        setShareUrl("");
        let cancelled = false;
        setPresetsLoading(true);
        getSharePresets("web")
            .then(async (data) => {
            if (cancelled)
                return;
            setPresets(data);
            const next = defaultEditorState(data);
            // Подгружаем шрифты каталога заранее, чтобы смена в селекте не мигала.
            await preloadShareFonts([
                next.greetingFont,
                next.captionFont,
                ...data.fonts.map((f) => f.key),
            ]);
            if (cancelled)
                return;
            setState(next);
        })
            .catch(() => {
            if (!cancelled)
                notifications.show({
                    title: "Не удалось загрузить пресеты",
                    message: "Оформление доступно частично.",
                    color: "red",
                });
        })
            .finally(() => {
            if (!cancelled)
                setPresetsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [opened]);
    // Локальные превью фото для живого рендера (до реальной загрузки на бэк).
    const photoUrls = React.useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
    React.useEffect(() => () => photoUrls.forEach((u) => URL.revokeObjectURL(u)), [photoUrls]);
    const previewConfig = React.useMemo(() => {
        const config = buildConfig(state);
        return photoUrls.length ? { ...config, photos: photoUrls } : config;
    }, [state, photoUrls]);
    const previewAudio = React.useMemo(() => ({
        id: audioId,
        url: audioUrl || "",
        cover_url: audioCoverUrl || null,
        title: audioTitle || null,
    }), [audioId, audioUrl, audioCoverUrl, audioTitle]);
    const togglePattern = (id) => patch({
        patternIds: state.patternIds.includes(id)
            ? state.patternIds.filter((x) => x !== id)
            : [...state.patternIds, id],
    });
    const handleCreate = async () => {
        const errs = validateEditor(state);
        if (errs.length) {
            notifications.show({ title: "Проверьте поля", message: errs.join(" "), color: "red" });
            return;
        }
        setPhase("saving");
        try {
            const share = await createShare({
                audio_id: audioId,
                type: state.type,
                config: buildConfig(state),
                use_instrumental: useInstrumental,
            });
            // Фото грузим по одному уже после создания шары (нужен share_id).
            for (const file of photos) {
                try {
                    await uploadSharePhoto(share.id, file);
                }
                catch {
                    notifications.show({
                        title: "Фото не загрузилось",
                        message: `«${file.name}» пропущено.`,
                        color: "red",
                    });
                }
            }
            const url = `${window.location.origin}/s/${share.token}`;
            setShareUrl(url);
            setPhase("done");
            trackEvent(AnalyticsEvent.SHARE_CLICK, { share_type: state.type, audio_id: audioId });
        }
        catch (e) {
            setPhase("edit");
            notifications.show({ title: "Не удалось создать", message: extractError(e), color: "red" });
        }
    };
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            notifications.show({ title: "Ссылка скопирована", message: shareUrl, color: "green" });
        }
        catch {
            notifications.show({ title: "Не удалось скопировать", message: shareUrl, color: "red" });
        }
    };
    const handleNativeShare = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share({ title: audioTitle || "Песня-подарок", url: shareUrl });
            }
            catch {
                /* пользователь отменил — игнор */
            }
        }
        else {
            void handleCopy();
        }
    };
    const isGift = state.type === "gift_page";
    const fontOptions = [
        { value: "", label: "— выберите шрифт —" },
        ...presets.fonts.map((f) => ({ value: f.key, label: f.name })),
    ];
    const animationOptions = [
        { value: "", label: "Без анимации" },
        ...presets.animations.map((a) => ({ value: a.key, label: a.name })),
    ];
    return (_jsx(Modal, { opened: opened, onClose: onClose, title: phase === "done"
            ? "Готово!"
            : useInstrumental
                ? "Поделиться минусом"
                : "Поделиться песней", size: "xl", closeOnBackdropClick: phase !== "saving", children: phase === "done" ? (_jsxs("div", { className: styles.done, children: [_jsx(FitScale, { className: styles.donePreview, children: _jsx(SharePreview, { type: state.type, config: previewConfig, presets: presets, fit: true }) }), _jsx("p", { className: styles.doneText, children: "\u041F\u0443\u0431\u043B\u0438\u0447\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443-\u043F\u043E\u0434\u0430\u0440\u043E\u043A \u0433\u043E\u0442\u043E\u0432\u0430. \u041E\u0442\u043F\u0440\u0430\u0432\u044C\u0442\u0435 \u0435\u0451 \u0442\u043E\u043C\u0443, \u043A\u043E\u043C\u0443 \u043F\u043E\u0441\u0432\u044F\u0449\u0435\u043D\u0430 \u043F\u0435\u0441\u043D\u044F." }), _jsx(TextField, { value: shareUrl, readOnly: true, onFocus: (e) => e.target.select() }), _jsxs("div", { className: styles.doneActions, children: [_jsx(Button, { onClick: handleCopy, variant: "outline", fullWidth: true, children: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C" }), _jsx(Button, { onClick: handleNativeShare, fullWidth: true, children: "\u041F\u043E\u0434\u0435\u043B\u0438\u0442\u044C\u0441\u044F" })] }), _jsx(Button, { variant: "subtle", onClick: onClose, fullWidth: true, children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" })] })) : (_jsxs("div", { className: styles.layout, children: [_jsxs("div", { className: styles.controls, children: [_jsx(SegmentedControl, { value: state.type, onChange: (t) => patch({ type: t }), options: [
                                { value: "gift_page", label: "Подарок" },
                                { value: "simple_page", label: "Простая" },
                            ], fullWidth: true }), presetsLoading ? (_jsxs("div", { className: styles.loadingRow, children: [_jsx(Loader, {}), " ", _jsx("span", { children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u0435\u2026" })] })) : (_jsxs(_Fragment, { children: [_jsx(Section, { title: "\u0424\u043E\u043D", children: _jsx(ColorField, { label: "\u0426\u0432\u0435\u0442 \u0444\u043E\u043D\u0430", value: state.background, onChange: (v) => patch({ background: v }), presets: presets.backgrounds }) }), _jsxs(Section, { title: "\u041F\u0430\u0442\u0442\u0435\u0440\u043D\u044B", children: [_jsx(ImagePresetPicker, { label: "\u0423\u0437\u043E\u0440\u044B", presets: presets.patterns, selected: state.patternIds, onToggle: togglePattern, multi: true }), state.patternIds.length > 0 && (_jsx(PatternControls, { size: state.patternSize, onSize: (v) => patch({ patternSize: v }), variety: state.patternVariety, onVariety: (v) => patch({ patternVariety: v }), maxVariety: state.patternIds.length }))] }), isGift ? (_jsxs(_Fragment, { children: [_jsxs(Section, { title: "\u041F\u043E\u0437\u0434\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435", children: [_jsx(TextChoiceField, { label: "\u0422\u0435\u043A\u0441\u0442", value: state.greetingText, onChange: (v) => patch({ greetingText: v }), presets: presets.greetings, multiline: true, placeholder: "\u0421 \u0434\u043D\u0451\u043C \u0440\u043E\u0436\u0434\u0435\u043D\u0438\u044F!" }), _jsx(ShareSelect, { label: "\u0428\u0440\u0438\u0444\u0442", options: fontOptions, value: state.greetingFont, onChange: (v) => patch({ greetingFont: v }) }), _jsx(ColorField, { label: "\u0426\u0432\u0435\u0442 \u0442\u0435\u043A\u0441\u0442\u0430", value: state.greetingColor, onChange: (v) => patch({ greetingColor: v }), presets: presets.text_colors }), _jsxs("label", { className: styles.sliderRow, children: [_jsxs("span", { children: ["\u0420\u0430\u0437\u043C\u0435\u0440: ", state.greetingSize] }), _jsx("input", { type: "range", min: 14, max: 80, value: state.greetingSize, onChange: (e) => patch({ greetingSize: Number(e.target.value) }) })] })] }), _jsxs(Section, { title: "\u041F\u043E\u0432\u043E\u0434", children: [_jsx(TitleChoiceField, { label: "\u041F\u043E\u0432\u043E\u0434", value: state.occasion, onChange: (v) => patch({ occasion: v }), presets: presets.occasions, placeholder: "\u0414\u0435\u043D\u044C \u0440\u043E\u0436\u0434\u0435\u043D\u0438\u044F" }), _jsx(ImagePresetPicker, { label: "\u0418\u043A\u043E\u043D\u043A\u0430 \u043F\u043E\u0432\u043E\u0434\u0430", presets: presets.icons, selected: state.iconId != null ? [state.iconId] : [], onToggle: (id) => patch({ iconId: state.iconId === id ? null : id }) })] }), _jsxs(Section, { title: "\u0410\u0434\u0440\u0435\u0441\u0430\u0442", children: [_jsx(TextField, { label: "\u041A\u043E\u043C\u0443 \u2014 \u0438\u043C\u044F \u043F\u043E\u043B\u0443\u0447\u0430\u0442\u0435\u043B\u044F", value: state.recipient, maxLength: MAX_TEXT, onChange: (e) => patch({ recipient: e.target.value }), placeholder: "\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440: \u041C\u0430\u0440\u0438\u044F" }), _jsx(TextField, { label: "\u041E\u0442 \u043A\u043E\u0433\u043E \u2014 \u0432\u0430\u0448\u0435 \u0438\u043C\u044F", value: state.sender, maxLength: MAX_TEXT, onChange: (e) => patch({ sender: e.target.value }), placeholder: "\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440: \u0418\u0432\u0430\u043D" })] }), _jsx(Section, { title: "\u041F\u043E\u0436\u0435\u043B\u0430\u043D\u0438\u0435", children: _jsx(TextChoiceField, { label: "\u0422\u0435\u043A\u0441\u0442 \u043F\u043E\u0436\u0435\u043B\u0430\u043D\u0438\u044F", value: state.wish, onChange: (v) => patch({ wish: v }), presets: presets.wishes, multiline: true, placeholder: "\u0421\u0447\u0430\u0441\u0442\u044C\u044F \u0438 \u0437\u0434\u043E\u0440\u043E\u0432\u044C\u044F!" }) }), _jsxs(Section, { title: "\u041A\u043E\u0440\u043E\u0431\u043A\u0430", children: [_jsx(ColorField, { label: "\u0426\u0432\u0435\u0442 \u043A\u043E\u0440\u043E\u0431\u043A\u0438", value: state.boxColorId != null ? { kind: "preset", id: state.boxColorId } : { kind: "none" }, onChange: (v) => patch({ boxColorId: v.kind === "preset" ? v.id : null }), presets: presets.box_colors, allowCustom: false }), _jsx(ShareSelect, { label: "\u0410\u043D\u0438\u043C\u0430\u0446\u0438\u044F \u043E\u0442\u043A\u0440\u044B\u0442\u0438\u044F", options: animationOptions, value: state.animationKey, onChange: (v) => patch({ animationKey: v }) })] })] })) : (_jsxs(Section, { title: "\u041F\u043E\u0434\u043F\u0438\u0441\u044C", children: [_jsx(TextField, { label: "\u0422\u0435\u043A\u0441\u0442 \u043F\u043E\u0434\u043F\u0438\u0441\u0438", value: state.captionText, maxLength: MAX_TEXT, onChange: (e) => patch({ captionText: e.target.value }), placeholder: "\u0421\u043B\u0443\u0448\u0430\u0439!" }), _jsx(ShareSelect, { label: "\u0428\u0440\u0438\u0444\u0442", options: fontOptions, value: state.captionFont, onChange: (v) => patch({ captionFont: v }) }), _jsx(ColorField, { label: "\u0426\u0432\u0435\u0442 \u0442\u0435\u043A\u0441\u0442\u0430", value: state.captionColor, onChange: (v) => patch({ captionColor: v }), presets: presets.text_colors })] })), _jsx(Section, { title: "\u0424\u043E\u0442\u043E", children: _jsx(PhotoPicker, { files: photos, onChange: setPhotos, max: EDITOR_PHOTO_LIMIT }) })] }))] }), _jsxs("div", { className: styles.previewPane, children: [_jsx("div", { className: styles.phoneFrame, children: _jsx(ShareStage, { type: state.type, config: previewConfig, presets: presets, audio: previewAudio, allowReplay: true }) }), _jsx(Button, { className: styles.createBtn, onClick: handleCreate, disabled: phase === "saving", leftSection: phase === "saving" ? _jsx(Loader, { size: "sm" }) : undefined, fullWidth: true, children: phase === "saving" ? "Создаём…" : "Создать ссылку" })] })] })) }));
};
